import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DroppableColumn from "./DroppableColumn";
import DraggableCard from "./DraggableCard";
import OrderHeader from "./OrderHeader";
import {
  fetchOrders,
  removeActiveOrder,
  updateOrderStatus,
} from "../../store/features/orderSlice";
import { MdLocalPrintshop } from "react-icons/md";
import "./index.scss";
import { toast } from "react-toastify";
import OrderList from "../OrderList";

const STATUSES = [
  { id: "new", title: "Новый", color: "#FFDC0090" },
  { id: "processing", title: "В обработке", color: "#FFDC0050" },
  { id: "shipped", title: "Отправлен", color: "#007BFB50" },
  { id: "closed", title: "Завершен", color: "#00D35950" },
  { id: "canceled", title: "Отменен", color: "#FF0C0C50" },
];

const Orders = () => {
  const dispatch = useDispatch();
  const { orders, activeOrder } = useSelector((state) => state.order);
  const [ordersList, setOrdersList] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [viewMode, setViewMode] = useState("kanban");

  useEffect(() => {
    dispatch(fetchOrders());
  }, [dispatch]);

  useEffect(() => {
    setOrdersList(orders);
  }, [orders]);

  useEffect(() => {
    if (activeOrder) {
      setSelectedStatus(activeOrder.status);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [activeOrder]);

  const handleStatusChange = async () => {
    if (selectedStatus && selectedStatus !== activeOrder.status) {
      try {
        const resultAction = await dispatch(
          updateOrderStatus({ id: activeOrder.id, status: selectedStatus })
        );

        if (updateOrderStatus.fulfilled.match(resultAction)) {
          toast.success(`Статус заказа №${activeOrder.key} успешно обновлен`, {
            closeButton: false,
          });
          dispatch(removeActiveOrder());
          dispatch(fetchOrders());
        } else {
          toast.error("Не удалось обновить статус заказа", {
            closeButton: false,
          });
        }
      } catch (error) {
        toast.error("Произошла ошибка при обновлении статуса", {
          closeButton: false,
        });
      }
    }
  };
  const formatDate = (date) => {
    const parsedDate = new Date(date);
    return isNaN(parsedDate)
      ? "Неизвестная дата"
      : parsedDate.toLocaleString("ru-RU", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  };
  const closeModal = () => dispatch(removeActiveOrder());

  const handlePrint = () => {
    const printWindow = window.open("", "", "width=800,height=600");

    const totalAmount = activeOrder.products?.reduce(
      (acc, item) => acc + item.product.price * item.quantity,
      0
    );

    const tableRows = activeOrder.products
      ?.map((product, index) => {
        const name = product.product.name;
        const unit = product.unit || "шт.";
        const price = product.product.price;
        const quantity = product.quantity;
        const sum = price * quantity;

        return `
          <tr>
            <td>${index + 1}</td>
            <td>${name}</td>
            <td>${unit}</td>
            <td>${price}</td>
            <td>${quantity}</td>
            <td>${sum}</td>
          </tr>
        `;
      })
      .join("");

    printWindow.document.write(`
        <html>
          <head>
            <title>Накладная к заказу №${activeOrder.key}</title>
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <style>
              body {
                font-family: 'Times New Roman', Times, serif;
                font-size: 11pt;
                padding: 20px;
                color: #000;
              }
              h2 {
                text-align: center;
                margin-bottom: 10px;
                font-size: 11pt;
              }
              .info {
                margin-bottom: 20px;
                font-size: 11pt;
              }
              .info p {
                margin: 4px 0;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-top: 10px;
                font-size: 11pt;
              }
              th, td {
                border: 1px solid #000;
                padding: 6px;
                text-align: center;
                font-size: 11pt;
              }
              th {
                background-color: #f2f2f2;
              }
              .total {
                margin-top: 15px;
                text-align: right;
                font-weight: bold;
                font-size: 11pt;
              }
              .signature-block {
                margin-top: 50px;
                display: flex;
                justify-content: space-between;
                font-size: 11pt;
              }
              .signature-line {
                width: 45%;
              }
              .signature-label {
                display: block;
                margin-bottom: 40px;
              }
      
              @media print {
                body {
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
            </style>
          </head>
          <body>
            <h2>Накладная к заказу №${activeOrder.key}</h2>
      
            <div class="info">
              <p><strong>Поставщик:</strong> ${activeOrder.owner.companyName}</p>
              <p><strong>Покупатель:</strong> ${activeOrder.user.lastName} ${activeOrder.user.firstName}</p>
              <p><strong>Адрес:</strong> ${activeOrder.user.lastName} ${activeOrder.user.firstName}</p>
            </div>
      
            <table>
              <thead>
                <tr>
                  <th>№</th>
                  <th>Наименование</th>
                  <th>Ед. изм.</th>
                  <th>Цена</th>
                  <th>Кол-во</th>
                  <th>Сумма</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
      
            <div class="total">Итого: ${totalAmount} сом</div>
      
            <div class="signature-block">
              <div class="signature-line">
                <span class="signature-label">Отправил / Отпустил: _______________</span>
              </div>
              <div class="signature-line">
                <span class="signature-label">Получил: _______________</span>
              </div>
            </div>
          </body>
        </html>
      `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <div className="order">
      <OrderHeader setViewMode={setViewMode} viewMode={viewMode} />

      {viewMode === "list" ? (
        <OrderList />
      ) : (
        <div className="kanban">
          {STATUSES.map(({ id, title, color }) => (
            <DroppableColumn key={id} id={id} title={title} color={color}>
              <div>
                {ordersList
                  .filter((order) => order.status === id)
                  .map((order) => (
                    <DraggableCard key={order.id} id={order.id} order={order} />
                  ))}
              </div>
            </DroppableColumn>
          ))}
        </div>
      )}

      {activeOrder && (
        <div className="order__modalOverlay">
          <div className="order__more more">
            <span className="more__close" onClick={closeModal}></span>
            <h3 className="more__title">
              Заказ: <span>№{activeOrder.key}</span>
            </h3>

            <div className="more__info">
              <div className="more__orderInfo">
                <p className="more__text more__price">
                  Общая стоимость заказа: <span>{activeOrder.amount}</span>
                </p>
                <p className="more__text more__date">
                  Дата создание заказа:{" "}
                  <span>{formatDate(activeOrder.createdAt)}</span>
                </p>
                <p className="more__text more__date">
                  Дата последнего обновления:{" "}
                  <span>{formatDate(activeOrder.updatedAt)}</span>
                </p>
              </div>
              <div className="more__addressInfo">
                <p className="more__text more__address">
                  Адрес доставки: <span>{activeOrder.shop?.region},</span>{" "}
                  <span>{activeOrder.shop?.district},</span>{" "}
                  <span>{activeOrder.shop?.street}</span>{" "}
                  <span>{activeOrder.shop?.shopNumber}</span>
                </p>
                <p className="more__text more__shopName">
                  Название магазина: <span>{activeOrder.shop?.shopName}</span>
                </p>
                <p className="more__text more__shopName">
                  ФИО:{" "}
                  <span>
                    {activeOrder.user.lastName} {activeOrder.user.firstName}
                  </span>
                </p>

                <p className="more__text more__shopName">
                  Телефон номер:{" "}
                  <span>{activeOrder.shop?.shopPhoneNumber}</span>
                </p>
              </div>
            </div>

            <div className="more__text more__status-block">
              <label htmlFor="status" className="status-label">
                Статус заказа:
              </label>
              <div className="status-select-group">
                <select
                  id="status"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="status-select"
                >
                  {STATUSES.filter(({ id }) => id !== "canceled").map(
                    ({ id, title }) => (
                      <option key={id} value={id}>
                        {title}
                      </option>
                    )
                  )}
                </select>
                <button
                  onClick={handleStatusChange}
                  className="btn-save-status"
                  disabled={selectedStatus === activeOrder.status}
                >
                  Обновить
                </button>
              </div>
            </div>

            <button className="btn-print" onClick={handlePrint}>
              Распечатать накладную
              <MdLocalPrintshop className="btn-print__icon" />
            </button>

            <div className="more__products">
              <table className="invoice-table">
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Наименование</th>
                    <th>Ед. изм.</th>
                    <th>Цена</th>
                    <th>Кол-во</th>
                    <th>Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {activeOrder.products?.map((product, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>
                        <div className="product-name">
                          {product.product.name}
                          <div className="product-preview">
                            <img
                              src={product.product.images[0]}
                              alt={product.product.name}
                              style={{ width: "100%" }}
                            />
                          </div>
                        </div>
                      </td>
                      <td>{product.unit || "шт."}</td>
                      <td>{product.product.price} сом</td>
                      <td>{product.quantity}</td>
                      <td>{product.product.price * product.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Orders;
